import React from "react";
import { Navigate } from "react-router-dom";
import MainLayout from "@/layouts/MainLayout";
import { useAuth } from "@/hooks/useAuth";
import { debugLogger } from '@/utils/debugLogger';
import { Button } from "@/components/ui/button";
import { RefreshCw, AlertCircle, Loader } from "lucide-react";

const ProtectedMainLayout: React.FC = () => {
  const { user, profileData, loading, error, retry } = useAuth();
  
  debugLogger.debug('ProtectedMainLayout - Render:', { 
    hasUser: !!user,
    loading,
    error,
    currentPath: window.location.pathname
  });

  // Check if account is deactivated and redirect to reactivate page
  if (user && profileData?.deactivated_at) {
    debugLogger.debug('ProtectedMainLayout - Account deactivated, redirecting to reactivate page');
    return <Navigate to="/reactivate-account" replace />;
  }

  // Se tem usuário, mostrar MainLayout
  if (user) {
    debugLogger.debug('ProtectedMainLayout - User found, rendering MainLayout');
    return <MainLayout />;
  }

  // SIMPLIFIED: Se tem erro, mostrar tela de erro
  if (error) {
    debugLogger.info('ProtectedMainLayout - Showing error state:', error);
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="mb-6">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Erro de Autenticação</h2>
            <p className="text-gray-400 mb-6">{error}</p>
          </div>
          
          <Button 
            onClick={retry}
            className="w-full bg-luxury-red hover:bg-luxury-red/80 text-white"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Tentar Novamente
          </Button>
          
          <Button 
            variant="ghost" 
            onClick={() => window.location.href = '/auth'}
            className="w-full mt-3 text-gray-400 hover:text-white"
          >
            Voltar ao Login
          </Button>
        </div>
      </div>
    );
  }

  // SIMPLIFIED: Se está loading, mostrar loading
  if (loading) {
    debugLogger.debug('ProtectedMainLayout - Showing loading state');
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <Loader className="h-16 w-16 text-luxury-red mx-auto mb-4 animate-spin" />
          <h2 className="text-xl font-semibold text-white mb-2">Verificando autenticação...</h2>
          <p className="text-gray-400">Aguarde um momento</p>
        </div>
      </div>
    );
  }

  // SIMPLIFIED: Se não tem usuário e não está loading, redirecionar
  debugLogger.debug('ProtectedMainLayout - No user and not loading, redirecting to auth');
  return <Navigate to="/auth" replace />;
};

export default ProtectedMainLayout;
